import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Moon, Sunrise, MessageCircle, Award, CreditCard, UserCog } from 'lucide-react';
import apiClient from '../../utils/api';
import './NotificationSettings.css';

function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    loadSettings();
    checkPushSubscription();
  }, []);

  const checkPushSubscription = async () => {
    if ('Notification' in window && Notification.permission === 'granted') {
      setPushEnabled(true);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await apiClient.getNotificationSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);

    try {
      setSaving(true);
      await apiClient.updateNotificationSettings({ [field]: value });
    } catch (error) {
      console.error('Error updating settings:', error);
      setSettings(settings); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleTimeChange = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);

    try {
      setSaving(true);
      await apiClient.updateNotificationSettings({ [field]: value });
    } catch (error) {
      console.error('Error updating time:', error);
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  const requestPermission = async () => {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setPushEnabled(true);
      handleToggle('enabled', true);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="notification-settings" data-testid="notification-settings">
      <div className="settings-section">
        <h3 className="section-title">🔔 Push-Benachrichtigungen</h3>
        
        {!pushEnabled ? (
          <div className="permission-request">
            <p className="permission-text">
              Aktivieren Sie Benachrichtigungen, um Erinnerungen und Updates zu erhalten.
            </p>
            <button
              className="btn-enable-push"
              onClick={requestPermission}
              data-testid="enable-push-button"
            >
              <Bell size={18} />
              Benachrichtigungen aktivieren
            </button>
          </div>
        ) : (
          <>
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">
                  <Bell size={20} />
                  <span>Benachrichtigungen aktiviert</span>
                </div>
                <div className="setting-description">
                  Master-Schalter für alle Benachrichtigungen
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings?.enabled || false}
                  onChange={(e) => handleToggle('enabled', e.target.checked)}
                  data-testid="notifications-enabled-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {settings?.enabled && (
              <>
                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      <Sunrise size={20} />
                      <span>Tägliche Routinen</span>
                    </div>
                    <div className="setting-description">
                      Erinnerungen für Morgen- und Abend-Frequenzen
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings?.daily_routine_enabled !== false}
                      onChange={(e) => handleToggle('daily_routine_enabled', e.target.checked)}
                      data-testid="daily-routine-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      <Award size={20} />
                      <span>Streak-Warnungen</span>
                    </div>
                    <div className="setting-description">
                      Erinnerung wenn Ihre Serie gefährdet ist
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings?.streak_alerts_enabled !== false}
                      onChange={(e) => handleToggle('streak_alerts_enabled', e.target.checked)}
                      data-testid="streak-alerts-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      <UserCog size={20} />
                      <span>Therapeuten-Mitteilungen</span>
                    </div>
                    <div className="setting-description">
                      Empfehlungen und Zugriffsanfragen
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings?.practitioner_notifications_enabled !== false}
                      onChange={(e) => handleToggle('practitioner_notifications_enabled', e.target.checked)}
                      data-testid="practitioner-notifications-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      <CreditCard size={20} />
                      <span>Abo-Erinnerungen</span>
                    </div>
                    <div className="setting-description">
                      Benachrichtigung wenn Abo bald abläuft
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings?.subscription_reminders_enabled !== false}
                      onChange={(e) => handleToggle('subscription_reminders_enabled', e.target.checked)}
                      data-testid="subscription-reminders-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      <MessageCircle size={20} />
                      <span>AI-Begleiter Nachrichten</span>
                    </div>
                    <div className="setting-description">
                      Proaktive Nachrichten vom ZENTA-Begleiter
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings?.chatbot_notifications_enabled !== false}
                      onChange={(e) => handleToggle('chatbot_notifications_enabled', e.target.checked)}
                      data-testid="chatbot-notifications-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      <MessageCircle size={20} />
                      <span>Stimmung bei Frequenzwechsel erfragen</span>
                    </div>
                    <div className="setting-description">
                      Nach jeder Frequenz-Nutzung wirst du gefragt, wie du dich fühlst (1-10 Skala). Dies hilft dem Begleiter, dich besser zu unterstützen.
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings?.mood_on_switch_enabled !== false}
                      onChange={(e) => handleToggle('mood_on_switch_enabled', e.target.checked)}
                      data-testid="mood-on-switch-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="quiet-hours-section">
                  <div className="section-header">
                    <Moon size={20} />
                    <span>Ruhezeiten</span>
                  </div>
                  <div className="quiet-hours-inputs">
                    <div className="time-input-group">
                      <label htmlFor="quiet-start">Von</label>
                      <input
                        id="quiet-start"
                        type="time"
                        value={settings?.quiet_hours_start || '22:00'}
                        onChange={(e) => handleTimeChange('quiet_hours_start', e.target.value)}
                        data-testid="quiet-hours-start"
                      />
                    </div>
                    <div className="time-input-group">
                      <label htmlFor="quiet-end">Bis</label>
                      <input
                        id="quiet-end"
                        type="time"
                        value={settings?.quiet_hours_end || '07:00'}
                        onChange={(e) => handleTimeChange('quiet_hours_end', e.target.value)}
                        data-testid="quiet-hours-end"
                      />
                    </div>
                  </div>
                  <p className="quiet-hours-hint">
                    Keine Benachrichtigungen während dieser Zeit
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {saving && (
        <div className="saving-indicator">
          Speichere...
        </div>
      )}
    </div>
  );
}

export default NotificationSettings;
